import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { openWorkspaceSocket } from "../lib/workspaceFeed";
import { MapPin, Copy, Bell, Send, Plus, RefreshCw, Trash2, Sun, Moon, Star, Circle, Cloud, Heart, Leaf, BedDouble, CheckCircle2 } from "lucide-react";

const ZONE_KINDS = [
  { value: "indoor", label: "Indoor" },
  { value: "outdoor", label: "Outdoor" },
  { value: "quiet", label: "Quiet" },
  { value: "transitional", label: "Transitional" },
  { value: "shared", label: "Shared" },
];

const KIND_COLOR = {
  indoor: "#E6BFA6",
  outdoor: "#9BA995",
  quiet: "#8A9984",
  transitional: "#E6BFA6",
  shared: "#9BA995",
};

const SIGNAL_ICON = {
  happy: Sun,
  calm: Circle,
  playful: Star,
  sleepy: Moon,
  shy: Cloud,
  curious: Leaf,
  need_hug: Heart,
  i_am_here: Circle,
};

const SIGNAL_PHRASE = {
  happy: "shared feeling happy",
  calm: "shared feeling calm",
  playful: "shared feeling playful",
  sleepy: "shared feeling sleepy",
  shy: "shared feeling shy",
  curious: "shared feeling curious",
  need_hug: "wanted a hug",
  i_am_here: "said I'm here",
};

export default function EnvironmentsPanel({ workspaceId, isAdmin }) {
  const [wall, setWall] = useState(null);
  const [zones, setZones] = useState([]);
  const [newZone, setNewZone] = useState({ name: "", kind: "indoor" });
  const [zoneBusy, setZoneBusy] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [calmBusy, setCalmBusy] = useState(false);
  const [calmMessage, setCalmMessage] = useState("");

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    const [w, z] = await Promise.all([
      api.get(`/workspaces/${workspaceId}/wall`),
      api.get(`/workspaces/${workspaceId}/zones`),
    ]);
    setWall(w.data);
    setZones(z.data);
  }, [workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live updates from the workspace channel
  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    const close = openWorkspaceSocket(workspaceId, (msg) => {
      if (!alive) return;
      if (
        msg.type === "zone" ||
        msg.type === "check_in" ||
        msg.type === "sensor" ||
        msg.type === "self_check_in"
      ) {
        // Lazy-refresh the wall
        refresh();
      }
    });
    return () => {
      alive = false;
      if (close) close();
    };
  }, [workspaceId, refresh]);

  const addZone = async (e) => {
    e.preventDefault();
    if (!newZone.name.trim()) return;
    setZoneBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/zones`, {
        name: newZone.name.trim(),
        kind: newZone.kind,
        color: KIND_COLOR[newZone.kind] || "#9BA995",
      });
      setNewZone({ name: "", kind: "indoor" });
      await refresh();
      toast("A new zone, gently added.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't add zone.");
    } finally {
      setZoneBusy(false);
    }
  };

  const deleteZone = async (zoneId) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/zones/${zoneId}`);
      await refresh();
      toast("Zone removed.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  const fetchWebhook = async () => {
    try {
      const r = await api.get(`/workspaces/${workspaceId}/webhook-key`);
      setWebhook(r.data);
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't load webhook key.");
    }
  };

  const rotateWebhook = async () => {
    try {
      const r = await api.post(`/workspaces/${workspaceId}/webhook-key/rotate`);
      setWebhook(r.data);
      toast("Rotated. Old key is now silent.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't rotate.");
    }
  };

  const copyWebhook = () => {
    if (!webhook) return;
    navigator.clipboard?.writeText(webhook.webhook_secret);
    toast("Copied.");
  };

  const sendCircleOfCalm = async () => {
    setCalmBusy(true);
    try {
      await api.post(`/workspaces/${workspaceId}/circle-of-calm`, {
        message: calmMessage.trim() || undefined,
      });
      setCalmMessage("");
      await refresh();
      toast("A circle of calm has rippled out to every parent.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't send.");
    } finally {
      setCalmBusy(false);
    }
  };

  if (!wall) return null;

  return (
    <div data-testid="environments-panel" className="space-y-12">
      {/* Zones */}
      <div>
        <div className="flex items-center gap-3">
          <MapPin size={14} strokeWidth={1.5} className="text-[#8A9984]" />
          <span className="eyebrow">Zones</span>
        </div>
        <h2 className="mt-4 font-serif text-3xl tracking-tight">
          The spaces inside {wall.workspace_name}.
        </h2>

        <div
          data-testid="environments-zones-grid"
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
        >
          {wall.zones.length === 0 && (
            <div className="bg-[#FBF9F6] p-7 sm:p-8 font-serif italic text-[#6A6863]">
              No zones yet.
            </div>
          )}
          {wall.zones.map((z) => {
            const childIds = wall.children_by_zone[z.id] || [];
            const childObjs = wall.children.filter((c) => childIds.includes(c.id));
            return (
              <div
                key={z.id}
                data-testid={`environments-zone-${z.id}`}
                className="bg-[#FBF9F6] p-7 sm:p-8"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: z.color }}
                    />
                    <span className="font-serif text-xl tracking-tight">{z.name}</span>
                  </div>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => deleteZone(z.id)}
                      data-testid={`environments-zone-delete-${z.id}`}
                      aria-label="Remove"
                      className="text-[#6A6863] hover:text-[#2C2B29] transition-colors p-1"
                    >
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-[#6A6863]">
                  {z.kind}
                </div>
                <div className="mt-6 space-y-2">
                  {childObjs.length === 0 ? (
                    <div className="font-serif italic text-[#6A6863]">empty</div>
                  ) : (
                    childObjs.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-3 text-[#2C2B29]"
                      >
                        <span
                          className="h-7 w-7 rounded-full bg-cover bg-center shrink-0"
                          style={{
                            backgroundImage: c.portrait_url
                              ? `url(${c.portrait_url})`
                              : undefined,
                            backgroundColor: "#E6BFA6",
                          }}
                        />
                        <span className="text-sm">{c.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {isAdmin && (
          <form
            onSubmit={addZone}
            data-testid="environments-add-zone-form"
            className="mt-8 rounded-3xl border hairline bg-white p-6 sm:p-8 flex flex-wrap gap-4 items-end"
          >
            <div className="flex-1 min-w-[180px]">
              <label className="eyebrow">A new zone</label>
              <input
                value={newZone.name}
                onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
                placeholder="e.g. Reading Corner"
                data-testid="environments-add-zone-name"
                className="orbit-input mt-3"
              />
            </div>
            <div>
              <label className="eyebrow">Kind</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {ZONE_KINDS.map((k) => (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => setNewZone({ ...newZone, kind: k.value })}
                    data-testid={`environments-add-zone-kind-${k.value}`}
                    className={`rounded-full px-3 py-1.5 text-[10px] tracking-widest uppercase border transition-colors duration-300 ${
                      newZone.kind === k.value
                        ? "bg-[#E6BFA6] text-[#2C2B29] border-[#E6BFA6]"
                        : "bg-transparent text-[#6A6863] border-[#2C2B29]/10"
                    }`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={zoneBusy || !newZone.name.trim()}
              data-testid="environments-add-zone-submit"
              className="btn-primary"
            >
              {zoneBusy ? "Adding…" : "Add"}
              <Plus size={14} strokeWidth={1.5} />
            </button>
          </form>
        )}
      </div>

      {/* Today's gentle transitions */}
      <div>
        <span className="eyebrow">Today's transitions</span>
        <ol
          data-testid="environments-checkins"
          className="mt-6 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
        >
          {wall.todays_check_ins.length === 0 && (
            <li className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]">
              No transitions yet today.
            </li>
          )}
          {wall.todays_check_ins.map((ci) => (
            <li
              key={ci.id}
              data-testid={`environments-checkin-${ci.id}`}
              className="bg-[#FBF9F6] p-5 flex items-center justify-between gap-4 flex-wrap"
            >
              <div>
                <div className="font-serif text-lg text-[#2C2B29]">
                  {ci.child_name}{" "}
                  <em className="italic text-[#8A9984]">
                    {ci.kind === "arrival" ? "arrived gently" : "headed home"}
                  </em>
                </div>
                <div className="text-xs text-[#6A6863] mt-1">
                  by {ci.by_user_name} ·{" "}
                  {new Date(ci.at).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Quiet voices from the children */}
      <AbsencesToday workspaceId={workspaceId} />
      <div>
        <span className="eyebrow">Quiet voices from the children</span>
        <p className="mt-2 text-sm text-[#6A6863] font-light max-w-xl">
          Tap-by-tap, what children chose to share today on their kiosk.
        </p>
        <ol
          data-testid="environments-self-checkins"
          className="mt-6 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
        >
          {(wall.todays_self_check_ins || []).length === 0 && (
            <li
              data-testid="environments-self-checkins-empty"
              className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]"
            >
              No voices yet today. The kiosk is open and quiet.
            </li>
          )}
          {(wall.todays_self_check_ins || []).map((sc) => {
            const Icon = SIGNAL_ICON[sc.signal] || Circle;
            const childObj = wall.children.find((c) => c.id === sc.child_id);
            const childName = childObj?.name || "a child";
            return (
              <li
                key={sc.id}
                data-testid={`environments-self-checkin-${sc.id}`}
                className="bg-[#FBF9F6] p-5 flex items-center gap-4"
              >
                <span
                  className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "#E6BFA6" }}
                >
                  <Icon size={16} strokeWidth={1.5} className="text-white" />
                </span>
                <div className="flex-1">
                  <div className="font-serif text-lg text-[#2C2B29]">
                    {childName}{" "}
                    <em className="italic text-[#8A9984]">
                      {SIGNAL_PHRASE[sc.signal] || sc.signal}.
                    </em>
                  </div>
                  <div className="text-xs text-[#6A6863] mt-1">
                    facilitated by {sc.by_user_name} ·{" "}
                    {new Date(sc.at).toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Recent sensors */}
      {wall.recent_sensors.length > 0 && (
        <div>
          <span className="eyebrow">Quiet signals from the building</span>
          <ol
            data-testid="environments-sensors"
            className="mt-6 space-y-2"
          >
            {wall.recent_sensors.slice(0, 8).map((s) => (
              <li
                key={s.id}
                className="text-sm text-[#6A6863] font-light"
              >
                <span className="text-[#2C2B29] capitalize">{s.kind}</span>{" "}
                {s.note ? `· ${s.note}` : ""}{" "}
                <span className="text-[#6A6863]/70">
                  · {new Date(s.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Circle of Calm */}
      {isAdmin && (
        <div>
          <div className="flex items-center gap-3">
            <Bell size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">Emergency reassurance</span>
          </div>
          <h2 className="mt-4 font-serif text-3xl tracking-tight">
            One gentle pulse, to all parents.
          </h2>
          <p className="mt-3 text-base text-[#6A6863] font-light max-w-xl">
            For the moments parents need to hear, all at once: <em className="italic text-[#8A9984]">all is well</em>.
          </p>
          <div className="mt-6 rounded-3xl border hairline bg-white p-6 sm:p-8">
            <textarea
              value={calmMessage}
              onChange={(e) => setCalmMessage(e.target.value)}
              placeholder="A line of reassurance (or leave blank for the default)…"
              data-testid="environments-circle-message"
              rows={2}
              className="orbit-input resize-none"
            />
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={sendCircleOfCalm}
                disabled={calmBusy}
                data-testid="environments-circle-send"
                className="btn-primary"
              >
                {calmBusy ? "Rippling…" : "Send a circle of calm"}
                <Send size={14} strokeWidth={1.5} />
              </button>
            </div>
            {wall.last_circle_of_calm && (
              <div className="mt-8 pt-6 border-t hairline">
                <span className="eyebrow">Last sent</span>
                <p className="mt-3 font-serif italic text-lg text-[#2C2B29]">
                  {wall.last_circle_of_calm.message}
                </p>
                <div className="mt-2 text-xs text-[#6A6863]">
                  {wall.last_circle_of_calm.triggered_by_name} ·{" "}
                  {new Date(wall.last_circle_of_calm.at).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Webhook key */}
      {isAdmin && (
        <div>
          <span className="eyebrow">Sensor webhook</span>
          <h2 className="mt-4 font-serif text-3xl tracking-tight">
            Let the building whisper, securely.
          </h2>
          <p className="mt-3 text-base text-[#6A6863] font-light max-w-xl">
            A shared secret your sensors include in the <code className="font-mono text-xs">X-Alouvya-Webhook-Secret</code> header when posting to <code className="font-mono text-xs">/api/webhooks/sensor</code>. Rotate at any time.
          </p>
          <div className="mt-6 rounded-3xl border hairline bg-white p-6 sm:p-8">
            {webhook ? (
              <div data-testid="environments-webhook-result">
                <code className="text-xs font-mono bg-[#FBF9F6] border hairline rounded-full px-4 py-2 text-[#2C2B29] break-all">
                  {webhook.webhook_secret}
                </code>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={copyWebhook}
                    data-testid="environments-webhook-copy"
                    className="btn-primary !py-2 !px-4 !text-xs"
                  >
                    Copy
                    <Copy size={12} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={rotateWebhook}
                    data-testid="environments-webhook-rotate"
                    className="btn-ghost !py-2 !px-4 !text-xs"
                  >
                    Rotate
                    <RefreshCw size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={fetchWebhook}
                data-testid="environments-webhook-show"
                className="btn-primary"
              >
                Reveal webhook key
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function AbsencesToday({ workspaceId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${workspaceId}/absences/today`);
      setItems(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspaceId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const ack = async (id) => {
    try {
      await api.post(`/absences/${id}/acknowledge`);
      await load();
      toast("Gently acknowledged.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't acknowledge.");
    }
  };

  return (
    <div>
      <span className="eyebrow">Resting at home today</span>
      <p className="mt-2 text-sm text-[#6A6863] font-light max-w-xl">
        A soft list of children whose families let us know they won&rsquo;t be in.
      </p>
      <ol
        data-testid="environments-absences-today"
        className="mt-6 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
      >
        {loading && (
          <li className="bg-[#FBF9F6] p-5 font-serif italic text-[#6A6863]">
            A quiet moment…
          </li>
        )}
        {!loading && items.length === 0 && (
          <li
            data-testid="environments-absences-empty"
            className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]"
          >
            Everyone is on their way in today.
          </li>
        )}
        {items.map((a) => (
          <li
            key={a.id}
            data-testid={`environments-absence-${a.id}`}
            className="bg-[#FBF9F6] p-5 flex items-start gap-4 flex-wrap"
          >
            <span
              className="mt-1 h-9 w-9 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "#D6CFE7" }}
            >
              <BedDouble size={16} strokeWidth={1.5} className="text-[#1A1B2E]" />
            </span>
            <div className="flex-1 min-w-[180px]">
              <div className="font-serif text-lg text-[#2C2B29]">
                {a.child_name}{" "}
                <em className="italic text-[#8A9984]">
                  {a.kind === "sick"
                    ? "resting (unwell)"
                    : a.kind === "vacation"
                    ? "on holiday"
                    : a.kind === "appointment"
                    ? "at an appointment"
                    : "absent"}
                  .
                </em>
              </div>
              <div className="text-xs text-[#6A6863] mt-1">
                shared by {a.reported_by_name}
                {a.note ? ` · ${a.note}` : ""}
              </div>
            </div>
            {a.acknowledged_at ? (
              <span
                data-testid={`environments-absence-ack-${a.id}`}
                className="trust-pill"
              >
                <CheckCircle2 size={12} strokeWidth={1.5} />
                acknowledged
              </span>
            ) : (
              <button
                type="button"
                onClick={() => ack(a.id)}
                data-testid={`environments-absence-acknowledge-${a.id}`}
                className="btn-ghost !py-1.5 !px-3 !text-[10px]"
              >
                Acknowledge
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
